import {
    type ReactNode,
    useContext,
    useMemo
} from "react";
import {
    Appearance
} from "react-native";
import {
    NCoreUIKitLocalize
} from "../core/hooks";
import {
    type ThemeContextStateType,
    type ThemePreferenceType,
    type GapPropagationType,
    type ThemeContextType,
    type SharpnessType,
    type ThemeConfigs,
    type ThemesType,
    type ThemeType
} from "../types";
import NCoreContext, {
    type ConfigType
} from "ncore-context";
import {
    subscribePointerType,
    getPointerType
} from "../helpers/pointer/usePointerType";
import {
    getLocaleTypography
} from "../helpers/theme/localeTypography";
import {
    mergeTypographyTokens
} from "../helpers/theme/typography";
import {
    mergePalettes
} from "../helpers/theme/palette";
import {
    mergeShapes
} from "../helpers/theme/shapes";
import {
    androidTypographyFixer
} from "../utils";
import suppressWebTransitions from "../helpers/motion/suppressWebTransitions";
import _defaultPaletteData from "../variants/themes/default.json";

const defaultPaletteData = androidTypographyFixer(_defaultPaletteData);

type ResolvedThemeConstantType = {
    themePreference: ThemePreferenceType;
};

type SystemThemeSubscriptionType = {
    remove: () => void;
};

type LocaleThemeStateConstantType = {
    state: ThemeContextType;
    locale?: string;
};

const localeThemeStateCache = new WeakMap<ThemeContextType, WeakMap<NCoreUIKit.Typography, ThemeContextType>>();

const getLocaleThemeState = ({
    locale,
    state
}: LocaleThemeStateConstantType): ThemeContextType => {
    const typography = getLocaleTypography({
        typography: state.typography,
        locale
    });

    if(typography === state.typography) {
        return state;
    }

    const localeStates = localeThemeStateCache.get(state) ?? new WeakMap<NCoreUIKit.Typography, ThemeContextType>();

    const cached = localeStates.get(typography);

    if(cached) {
        return cached;
    }

    const localeState = {
        ...state,
        typography
    };

    localeStates.set(typography, localeState);

    localeThemeStateCache.set(state, localeStates);

    return localeState;
};

const getSystemTheme = (): ThemeType => {
    return Appearance.getColorScheme() === "dark" ? "dark" : "light";
};

const getResolvedTheme = ({
    themePreference
}: ResolvedThemeConstantType): keyof NCoreUIKit.ThemeKey => {
    if(themePreference === "system") {
        return getSystemTheme();
    }

    return themePreference;
};

class NCoreUIKitTheme<T extends ThemesType> extends NCoreContext<ThemeContextType, ConfigType<ThemeContextType>> {
    projectThemes?: NCoreUIKit.PartialPalette = defaultPaletteData;

    systemThemeSubscription?: SystemThemeSubscriptionType;

    useContext = ({
        gapPropagation,
        paletteKey,
        sharpness,
        themeKey
    }: {
        paletteKey?: keyof NCoreUIKit.PaletteKey;
        themeKey?: keyof NCoreUIKit.ThemeKey;
        gapPropagation?: GapPropagationType;
        sharpness?: SharpnessType;
    } = {}, customLocalize?: {
        activeLocale?: keyof NCoreUIKit.LocaleKey;
    }): ThemeContextType => {
        const currentState = useContext(this.stateContext);

        const localizeState = useContext(NCoreUIKitLocalize.stateContext);

        const activeLocale = customLocalize?.activeLocale ? NCoreUIKitLocalize.prepare({
            activeLocale: customLocalize.activeLocale
        }).activeLocale : localizeState.activeLocale;

        if(
            gapPropagation ||
            paletteKey ||
            sharpness ||
            themeKey
        ) {
            const activeState = this.prepare({
                activeGapPropagation: gapPropagation ? gapPropagation : currentState.activeGapPropagation,
                activeSharpness: sharpness ? sharpness : currentState.activeSharpness,
                activePalette: paletteKey ? paletteKey : currentState.activePalette,
                activeTheme: themeKey ? themeKey : currentState.activeTheme
            });

            return getLocaleThemeState({
                state: {
                    ...currentState,
                    ...activeState,
                    activeThemePreference: themeKey ? themeKey : currentState.activeThemePreference
                },
                locale: activeLocale
            });
        }

        return getLocaleThemeState({
            locale: activeLocale,
            state: currentState
        });
    };

    SurfaceProvider = ({
        surfaceColor,
        children
    }: {
        surfaceColor: keyof NCoreUIKit.ContainerContentColors;
        children?: ReactNode;
    }) => {
        const StateContextRenderer = this.stateContext;

        const currentState = useContext(this.stateContext);

        const value = useMemo(() => ({
            ...currentState,
            surfaceColor
        }), [
            currentState,
            surfaceColor
        ]);

        return <StateContextRenderer.Provider
            value={value}
        >
            {children}
        </StateContextRenderer.Provider>;
    };

    constructor(initialState: T) {
        const {
            initialSelectedActiveSharpness,
            initialSelectedGapPropagation,
            initialSelectedPalette,
            initialSelectedTheme,
            projectThemes
        } = initialState;

        const initialThemePreference = initialSelectedTheme ?? "dark";

        const initialResolvedTheme = getResolvedTheme({
            themePreference: initialThemePreference
        });

        const initialPalette = defaultPaletteData.palettes.find(p => p.name === (initialSelectedPalette ?? "nibgat")) ?? defaultPaletteData.palettes[0]!;

        let initialTheme = initialPalette.themes[initialResolvedTheme as keyof typeof initialPalette.themes];

        if(!initialTheme) {
            initialTheme = initialPalette.themes.dark ?? Object.values(initialPalette.themes)[0]!;
        }

        super({
            paletteKeys: defaultPaletteData.palettes.map(p => p.name) as Array<keyof NCoreUIKit.PaletteKey>,
            themeKeys: Object.keys(initialPalette.themes) as Array<keyof NCoreUIKit.ThemeKey>,
            controlPaddings: defaultPaletteData.shapes.controlPaddings.spacious,
            activeGapPropagation: initialSelectedGapPropagation ?? "spacious",
            controlHeights: defaultPaletteData.shapes.controlHeights.spacious,
            activeSharpness: initialSelectedActiveSharpness ?? "soft",
            shadowAlpha: defaultPaletteData.shapes.shadowAlpha.dark,
            inlineSpaces: defaultPaletteData.shapes.inlineSpaces,
            shapeConfigs: defaultPaletteData.shapes.shapeConfigs,
            touchTargets: defaultPaletteData.shapes.touchTargets,
            avatarSizes: defaultPaletteData.shapes.avatarSizes,
            typography: defaultPaletteData.typography.spacious,
            activePalette: initialSelectedPalette ?? "nibgat",
            radiuses: defaultPaletteData.shapes.radiuses.soft,
            spaces: defaultPaletteData.shapes.spaces.spacious,
            elevations: defaultPaletteData.shapes.elevations,
            durations: defaultPaletteData.shapes.durations,
            iconSizes: defaultPaletteData.shapes.iconSizes,
            activeThemePreference: initialThemePreference,
            borders: defaultPaletteData.shapes.borders,
            resolvedGapPropagation: "spacious",
            activeTheme: initialResolvedTheme,
            surfaceColor: "default",
            colors: initialTheme,
            configs: {
                headerSpace: 0
            }
        }, {
            key: "NCoreUIKitTheme"
        });

        this.projectThemes = projectThemes;

        const activeState = this.prepare({
            activeGapPropagation: initialSelectedGapPropagation ?? "spacious",
            activeSharpness: initialSelectedActiveSharpness ?? "soft",
            activePalette: initialSelectedPalette ?? "nibgat",
            activeTheme: initialThemePreference
        });

        this.state = {
            ...activeState,
            surfaceColor: "default"
        } as ThemeContextType;

        subscribePointerType(() => {
            this.setState(this.prepare());
        });

        this.handleSystemThemeSubscription(initialThemePreference);
    }

    handleSystemThemeSubscription = (themePreference: ThemePreferenceType) => {
        if(themePreference !== "system") {
            this.systemThemeSubscription?.remove();

            this.systemThemeSubscription = undefined;

            return;
        }

        if(this.systemThemeSubscription) {
            return;
        }

        this.systemThemeSubscription = Appearance.addChangeListener(this.onSystemThemeChange);
    };

    onSystemThemeChange = () => {
        if(this.state.activeThemePreference !== "system") {
            return;
        }

        suppressWebTransitions();

        this.setState(this.prepare());
    };

    updateConfigs = (configs: Partial<ThemeConfigs>) => {
        this.setState({
            configs: {
                ...this.state.configs,
                ...configs
            }
        });
    };

    prepare = (props?: {
        activePalette?: keyof NCoreUIKit.PaletteKey;
        activeGapPropagation?: GapPropagationType;
        activeTheme?: ThemePreferenceType;
        activeSharpness?: SharpnessType;
    }): ThemeContextStateType => {
        const gapPropagation = props && props.activeGapPropagation ? props.activeGapPropagation : this.state.activeGapPropagation;
        const sharpness = props && props.activeSharpness ? props.activeSharpness : this.state.activeSharpness;
        const palette = props && props.activePalette ? props.activePalette : this.state.activePalette;
        const themePreference = props && props.activeTheme ? props.activeTheme : this.state.activeThemePreference;

        const theme = getResolvedTheme({
            themePreference
        });

        const resolvedGapPropagation = gapPropagation === "compact" && getPointerType() === "coarse" ? "spacious" : gapPropagation;

        const defaultPalette = defaultPaletteData.palettes.find(p => p.name === palette) ?? defaultPaletteData.palettes[0]!;
        const defaultTheme = defaultPalette.themes[theme as keyof typeof defaultPalette.themes] ?? defaultPalette.themes.dark;
        const defaultShadowAlpha = defaultPaletteData.shapes.shadowAlpha[theme as keyof NCoreUIKit.ShadowAlphaWithVariants] ?? defaultPaletteData.shapes.shadowAlpha.dark;

        const defaultState: ThemeContextStateType = {
            paletteKeys: defaultPaletteData.palettes.map(p => p.name) as Array<keyof NCoreUIKit.PaletteKey>,
            controlPaddings: defaultPaletteData.shapes.controlPaddings[resolvedGapPropagation],
            themeKeys: Object.keys(defaultPalette.themes) as Array<keyof NCoreUIKit.ThemeKey>,
            controlHeights: defaultPaletteData.shapes.controlHeights[resolvedGapPropagation],
            typography: defaultPaletteData.typography[resolvedGapPropagation],
            spaces: defaultPaletteData.shapes.spaces[resolvedGapPropagation],
            radiuses: defaultPaletteData.shapes.radiuses[sharpness],
            inlineSpaces: defaultPaletteData.shapes.inlineSpaces,
            shapeConfigs: defaultPaletteData.shapes.shapeConfigs,
            touchTargets: defaultPaletteData.shapes.touchTargets,
            avatarSizes: defaultPaletteData.shapes.avatarSizes,
            elevations: defaultPaletteData.shapes.elevations,
            durations: defaultPaletteData.shapes.durations,
            iconSizes: defaultPaletteData.shapes.iconSizes,
            resolvedGapPropagation: resolvedGapPropagation,
            borders: defaultPaletteData.shapes.borders,
            activeThemePreference: themePreference,
            activeGapPropagation: gapPropagation,
            shadowAlpha: defaultShadowAlpha,
            configs: this.state.configs,
            activeSharpness: sharpness,
            activePalette: palette,
            colors: defaultTheme,
            activeTheme: theme
        };

        if(!this.projectThemes) {
            return defaultState;
        }

        const projectPalettes = (this.projectThemes.palettes ?? []) as NCoreUIKit.ThemePalettes;

        const shapes = mergeShapes({
            projectShapes: this.projectThemes.shapes as NCoreUIKit.Shapes,
            defaultShapes: defaultPaletteData.shapes
        });

        const shadowAlpha = shapes.shadowAlpha[theme as keyof NCoreUIKit.ShadowAlphaWithVariants] ?? shapes.shadowAlpha.dark;

        const typography = mergeTypographyTokens({
            projectTypographyTokens: this.projectThemes.typography as NCoreUIKit.TypographyWithVariants,
            defaultTypographyTokens: defaultPaletteData.typography
        });

        const colors = mergePalettes(
            defaultPaletteData.palettes,
            projectPalettes,
            palette,
            theme
        );

        const dPKeys = [
            ...new Set([
                ...Object.keys(defaultPaletteData.palettes[0]?.themes ?? {}),
                ...Object.keys(projectPalettes[0]?.themes ?? {})
            ])
        ];

        const dPPKeys = [
            ...new Set([
                ...defaultPaletteData.palettes.map(p => p.name),
                ...projectPalettes.map(p => p.name)
            ])
        ];

        const newState: ThemeContextStateType = {
            controlPaddings: shapes.controlPaddings[resolvedGapPropagation],
            controlHeights: shapes.controlHeights[resolvedGapPropagation],
            paletteKeys: dPPKeys as Array<keyof NCoreUIKit.PaletteKey>,
            themeKeys: dPKeys as Array<keyof NCoreUIKit.ThemeKey>,
            resolvedGapPropagation: resolvedGapPropagation,
            typography: typography[resolvedGapPropagation],
            spaces: shapes.spaces[resolvedGapPropagation],
            activeThemePreference: themePreference,
            activeGapPropagation: gapPropagation,
            radiuses: shapes.radiuses[sharpness],
            inlineSpaces: shapes.inlineSpaces,
            shapeConfigs: shapes.shapeConfigs,
            touchTargets: shapes.touchTargets,
            avatarSizes: shapes.avatarSizes,
            elevations: shapes.elevations,
            configs: this.state.configs,
            durations: shapes.durations,
            iconSizes: shapes.iconSizes,
            activeSharpness: sharpness,
            shadowAlpha: shadowAlpha,
            borders: shapes.borders,
            activePalette: palette,
            activeTheme: theme,
            colors: colors
        };

        return newState;
    };

    switch = ({
        gapPropagation,
        paletteKey,
        sharpness,
        themeKey
    }: {
        paletteKey?: keyof NCoreUIKit.PaletteKey;
        gapPropagation?: GapPropagationType;
        themeKey?: ThemePreferenceType;
        sharpness?: SharpnessType;
    }) => {
        const activeState = this.prepare({
            activeGapPropagation: gapPropagation ?? this.state.activeGapPropagation,
            activeTheme: themeKey ?? this.state.activeThemePreference,
            activeSharpness: sharpness ?? this.state.activeSharpness,
            activePalette: paletteKey ?? this.state.activePalette
        });

        if(themeKey) {
            this.handleSystemThemeSubscription(themeKey);
        }

        if(themeKey || paletteKey) {
            suppressWebTransitions();
        }

        this.setState(activeState);
    };

    updateProjectTheme = (newProjectThemes: NCoreUIKit.PartialPalette, newState?: {
        activePalette?: keyof NCoreUIKit.PaletteKey;
        activeGapPropagation?: GapPropagationType;
        activeTheme?: ThemePreferenceType;
        activeSharpness?: SharpnessType;
    }) => {
        this.projectThemes = newProjectThemes;
        const activeState = this.prepare(newState);

        if(newState && newState.activeTheme) {
            this.handleSystemThemeSubscription(newState.activeTheme);
        }

        suppressWebTransitions();

        this.setState(activeState);
    };
}
export default NCoreUIKitTheme;
